import {
  ActionsColumnValues,
  ActionsEvents,
  DEVICES_CLASS,
  DEVICES_TYPES_ORDER,
  OS_CLASS,
  OS_TYPE,
  ScoreLevels,
  SelectionEvents,
  Watchlist,
} from './constants';
import { translateFn } from '../../utils/translator.helper';
import { GzTooltipWebComponent } from '../../ui/gzTooltip';
import { GzIconLabelWebComponent } from '../../ui/gzIconLabel';
import { BaseWebComponentTranslator } from '../../framework/BaseWebComponentTranslator';
import { OperatingSystemType } from '@nirvana/shared/dtos/types/protected-entities/common';
import { getBulletColorForScore, getWatchlistButtonTextBasedOnState } from './utils';
import { html, render, TemplateResult } from 'lit';
import { GzSelectWebComponent } from '../../ui/gzSelect';
import { SelectEvents } from '../../ui/gzSelect/types';
import { FontSizes, FontWeights } from '../../types/common/types';
import { UserType } from '../usersApp/types';
import { GzIconWebComponent } from '../../ui/gzIcon';
import { ButtonColor, FontSize } from '../../ui/gzIconButton/types';
import { entityIconSet } from '../../theme/entity-icons';
import { isEmpty } from 'lodash';
import { WEB_COMPONENT_GZ_ICON_LABEL } from '../../webcomponents.constants';
import { USERS_TYPES_ORDER } from '../userBehaviorRisksApp/types';


export function riskScoreRenderer(rowData) {
  const score = rowData?.item?.scoreBreakdown.finalScore;
  const overlay = this.dom.$overlay;

  const text = getTextForScore(score);

  const color = getBulletColorForScore(score);
  if (score > 0) {
    return html`
      <div
        class="full-height-cell"
        @mouseenter="${e => buildOverlayContent(e, overlay, rowData.item, this.riskType)}"
        @mouseleave="${e => overlay.close()}"
      >
        <gz-bullet color="${color}"><span>${text} (${score}%)</span></gz-bullet>
      </div>
    `;
  } else {
    return html`
      <div class="full-height-cell">
        <gz-bullet color="${color}"><span>${score}%</span></gz-bullet>
      </div>
    `;
  }
}

export function getTextForScore(score) {
  let text = '';
  switch (true) {
    case score >= ScoreLevels.HIGH:
      text = translateFn('High');
      break;
    case score >= ScoreLevels.MEDIUM && score < ScoreLevels.HIGH:
      text = translateFn('Medium');
      break;
    case score >= ScoreLevels.LOW && score < ScoreLevels.MEDIUM:
      text = translateFn('Low');
      break;
    default:
      text = translateFn('N/A');
      break;
  }

  return text;
}

export async function closeOverlay(overlay) {
  overlay = await overlay;
  if (overlay) {
    overlay.close();
  }
}

export async function buildOverlayContent(e, overlay, data, riskType) {
  overlay = await overlay;
  if (overlay) {
    overlay.renderer = (root: HTMLElement) => {
      // Create a new div element
      const div = document.createElement('div');

      // Clear any existing child nodes in the root
      if (root.firstChild) root.removeChild(root.firstChild);

      // Render the template into the div
      render(renderContextMenuContent(data, riskType), div);

      // Append the div to the root
      root.appendChild(div);
    };
    overlay.openOverlay({ x: e.pageX, y: e.pageY });
  }
}

function renderContextMenuContent(data, riskType): TemplateResult {
  const finalScore = data.scoreBreakdown.finalScore;
  const baseScore: number = <number>data.scoreBreakdown.baseScore;
  const formattedData = new Array({
    label: translateFn('Base score'),
    value: (+baseScore.toFixed(2)).toString(),
  });
  for (const [key, value] of Object.entries(data.scoreBreakdown.multiplier)) {
    if (value !== null) {
      const score: number = <number>value;

      formattedData.push({
        label: BaseWebComponentTranslator.getInstance().getRiskManagementScoreBreakdown(key, riskType),
        value: '+' + +score.toFixed(2),
      });
    }
  }

  const color = getBulletColorForScore(finalScore);

  return html`
    <gz-double-column-details .items="${formattedData}">
      <div slot="header-content-left">
        <gz-bullet color="${color}">
          <gz-text fontSize="${FontSizes.PARAGRAPH_12}" fontWeight="${FontWeights.MEDIUM}"
            >${getTextForScore(finalScore)}
          </gz-text>
        </gz-bullet>
      </div>

      <div slot="header-content-right">
        <gz-text fontSize="${FontSizes.PARAGRAPH_12}" fontWeight="${FontWeights.BOLD}">${finalScore}</gz-text>
      </div>
    </gz-double-column-details>
  `;
}

export function renderRiskScore(rowData, overlay, riskType) {
  const score = rowData.scoreBreakdown.finalScore;
  const text = getTextForScore(score);

  const color = getBulletColorForScore(score);

  if (score > 0) {
    return html`
      <div
        class="full-height-cell"
        @mouseenter="${e => buildOverlayContent(e, overlay, rowData, riskType)}"
        @mouseleave="${e => closeOverlay(overlay)}"
      >
        <gz-bullet color="${color}"><span style="pointer-events: all;">${text} (${score}%)</span></gz-bullet>
      </div>
    `;
  } else {
    return html`
      <div class="full-height-cell">
        <gz-bullet color="${color}"><span>${score}%</span></gz-bullet>
      </div>
    `;
  }
}

export function complianceRenderer(rowData) {
  return renderCompliance(rowData?.item?.compliance);
}

export function renderCompliance(compliance) {
  if (!compliance || compliance.length === 0) {
    return '-';
  }

  const translator = BaseWebComponentTranslator.getInstance();
  return html`<span class="cell-label">
    ${compliance.map(complianceId => translator.getRiskManagementCompliance(complianceId)).join(', ')}
  </span>`;
}

export function companyRenderer(rowData) {
  const companyName = rowData?.item?.company.name;
  return html`<span class="cell-label">${companyName}</span>`;
}

export function usernameRenderer(rowData) {
  return renderAsCellLabel(rowData?.item?.username);
}

export function fqdnRenderer(rowData) {
  return renderAsCellLabel(rowData?.item?.fqdn);
}

export function ipRenderer(rowData) {
  return renderAsCellLabel(rowData?.item?.ip);
}

export function renderAsCellLabel(text) {
  if (!text) {
    return '-';
  }

  return html`<span class="cell-label">${text}</span>`;
}

export function affectedDevicesRenderer(rowData) {
  return riskManagementEntitiesRenderer.call(
    this,
    rowData?.item,
    rowData?.item?.affectedDevices,
    DEVICES_TYPES_ORDER,
    DEVICES_CLASS,
    BaseWebComponentTranslator.getInstance().getDeviceTypes
  );
}

export function osRenderer(rowData) {
  return renderOsIcon(rowData?.item?.operatingSystem);
}

export function regionRenderer(rowData) {
  return html`<span class="cell-label">${rowData?.item?.region ? rowData?.item?.region : '-'}</span>`;
}

export function regionsRenderer(rowData) {
  const regions = rowData?.item?.regions ?? [];
  return regions.length === 0 ? '-' : html`<span class="cell-label">${regions.join(', ')}</span>`;
}

export function resourceIdRenderer(rowData) {
  const resourceId = rowData?.item?.resourceId ? rowData?.item?.resourceId : '-';
  return html`<span class="cell-label">${resourceId}</span>`;
}

function renderOsIcon(os: OperatingSystemType) {
  if (os === OS_TYPE.UNKNOWN) {
    return '-';
  }
  const gzIconElement = new GzIconWebComponent();
  gzIconElement.name = `ds-${OS_CLASS.get(os)}`;
  gzIconElement.size = 20;
  gzIconElement.color = ButtonColor.SECONDARY;
  gzIconElement.setCustomIconSet(entityIconSet);

  return html`
    <gz-tooltip tooltiptext=${BaseWebComponentTranslator.getInstance().getOperatingSystem(os)}>
      ${gzIconElement}
    </gz-tooltip>
  `;
}

export function osesRenderer(rowData) {
  const oses = rowData?.item?.operatingSystems ?? [];
  if (oses.length === 0 || (oses.length === 1 && oses[0] === OS_TYPE.UNKNOWN)) {
    return '-';
  }

  const data = new GzTooltipWebComponent();
  data.classList.add('cell-label');
  data.classList.add('list-buttons');
  data.setAttribute('part', 'item-group-icons');

  const tooltip: Array<string> = [];

  for (const os of oses) {
    if (os === OS_TYPE.UNKNOWN) {
      continue;
    }
    tooltip.push(`${BaseWebComponentTranslator.getInstance().getOperatingSystem(os)}`);
    const gzIcon = new GzIconLabelWebComponent();
    gzIcon.setCustomIconSet(entityIconSet);
    gzIcon.icon = `ds-${OS_CLASS.get(os)}`;
    gzIcon.size = FontSize.ICON_SIZE_20;
    gzIcon.setAttribute('part', 'grid-cell-icon');
    gzIcon.iconColor = 'var(--chart-302-icon-buttons-titles)';
    data.appendChild(gzIcon);
  }

  data.isEllipsis = false;
  data.tooltipText = tooltip.join(', ');

  return data;
}

export function riskManagementEntitiesRenderer(
  rowDataItem,
  entities: Array<any>,
  entitiesOrder: Array<UserType>,
  iconClassMap: Map<UserType, string>,
  translateEntity: Function
) {
  if (!entities || !entities.length) {
    return '-';
  }

  const map = {};
  for (const entity of entities) {
    const { type, ids } = entity;
    map[type] = ids;
  }

  const data = new GzTooltipWebComponent();
  data.classList.add('cell-label');
  data.classList.add('list-buttons');
  data.setAttribute('part', 'item-group-icons');
  data.isEllipsis = true;

  const tooltip: Array<string> = [];

  for (const type of entitiesOrder) {
    if (!map[type]) {
      continue;
    }

    if (map[type].length === 0) {
      continue;
    }

    const label = map[type].length < 100 ? map[type].length : '99+';

    tooltip.push(`${translateEntity(type)}: ${map[type].length}`);
    const gzIcon = new GzIconLabelWebComponent();
    gzIcon.setCustomIconSet(entityIconSet);
    gzIcon.label = label;
    gzIcon.icon = iconClassMap.get(type);
    gzIcon.size = FontSize.ICON_SIZE_20;
    gzIcon.iconColor = 'var(--chart-302-icon-buttons-titles)';
    gzIcon.setAttribute('part', 'grid-cell-icon');
    if (entitiesOrder === USERS_TYPES_ORDER) {
      gzIcon.addEventListener('click', () => {
        handleAffectedUsersIconClicked(this, type, rowDataItem, gzIcon, entities);
      });
    } else {
      gzIcon.addEventListener('click', () => {
        handleAffectedDeviceIconClicked(this, type, rowDataItem, gzIcon, entities);
      });
    }

    data.appendChild(gzIcon);
  }

  data.isEllipsis = false;
  data.tooltipText = tooltip.join(', ');

  if (!data.hasChildNodes()) {
    return html`<span class="cell-label">-</span>`;
  }

  return data;
}

export function handleAffectedDeviceIconClicked(component, iconType, rowData, gzIcon, entities) {
  removeActiveStateFromGzIconFromAffectedDevices(component);
  gzIcon.active = true;

  const affectedResourcesIds: any = [];
  entities.forEach(device => {
    affectedResourcesIds.push(...device.ids);
  });

  component.dom.$grid.dispatchEvent(
    new CustomEvent(SelectionEvents.RESOURCE_ICON_CLICK, {
      detail: {
        iconType,
        affectedResourcesIds,
        companyId: rowData.companyId,
      },
      bubbles: true,
      composed: true,
    })
  );
}

export function handleAffectedUsersIconClicked(component, iconType, rowData, gzIcon, entities) {
  removeActiveStateFromGzIconFromAffectedUsers(component);
  gzIcon.active = true;

  const affectedIdentitiesIds: any = [];
  entities.forEach(identitiesDetails => {
    affectedIdentitiesIds.push(...identitiesDetails.ids);
  });

  component.dom.$grid.dispatchEvent(
    new CustomEvent(SelectionEvents.IDENTITY_ICON_CLICK, {
      detail: {
        iconType,
        affectedIdentitiesIds,
        companyId: rowData.companyId,
      },
      bubbles: true,
      composed: true,
    })
  );
}

export function removeActiveStateFromGzIconFromAffectedDevices(component) {
  component.dom.$grid.shadowRoot?.querySelectorAll(WEB_COMPONENT_GZ_ICON_LABEL).forEach(iconLabel => {
    iconLabel.active = false;
  });
}

export function removeActiveStateFromGzIconFromAffectedUsers(component) {
  component.dom.$grid.shadowRoot?.querySelectorAll(WEB_COMPONENT_GZ_ICON_LABEL).forEach(iconLabel => {
    iconLabel.active = false;
  });
}

export function selectColumnRenderer(rowData, extraOptions: Array<any> = []) {
  const options = [] as Array<any>;

  options.push(...extraOptions);

  if (rowData.item.watchlist === Watchlist.YES) {
    options.push({
      title: getWatchlistButtonTextBasedOnState(Watchlist.YES),
      value: ActionsColumnValues.REMOVE_FROM_WATCHLIST,
      event: {
        name: ActionsEvents.WATCHLIST_CHANGED,
        detail: {
          ids: [rowData.item._id],
          newWatchlist: Watchlist.NO,
          companyId: rowData.item.companyId,
        },
      },
    });
  } else {
    options.push({
      title: getWatchlistButtonTextBasedOnState(Watchlist.NO),
      value: ActionsColumnValues.ADD_TO_WATCHLIST,
      event: {
        name: ActionsEvents.WATCHLIST_CHANGED,
        detail: {
          ids: [rowData.item._id],
          newWatchlist: Watchlist.YES,
          companyId: rowData.item.companyId,
        },
      },
    });
  }

  const gzSelect = new GzSelectWebComponent();
  const cellContainer = document.createElement('div');
  cellContainer.className = 'd-flex justify-end w-full';

  gzSelect.options = options.map(option => {
    return {
      title: option.title,
      value: option.value,
      disabled: option.disabled ?? false,
    };
  });
  gzSelect.innerHTML = ` <gz-icon-button
          icon="ds-icon-more"
          fontSize="24"
          buttonType="square"
          buttonFill="quiet"
          buttonColor="secondary">
     </gz-icon-button>`;
  gzSelect.classList.add('inline-menu');

  gzSelect.addEventListener(SelectEvents.SELECTION_CHANGED, (e: CustomEvent) => {
    const selectedValue = e.detail.selected;

    const selectedOption = options.find(option => option.value === selectedValue);

    if (!selectedOption) {
      return;
    }

    this.dom.$grid.dispatchEvent(
      new CustomEvent(selectedOption.event.name, {
        detail: selectedOption.event.detail,
        bubbles: true,
        composed: true,
      })
    );
  });

  cellContainer.appendChild(gzSelect);

  return cellContainer;
}
